import { useState, useEffect, useCallback } from 'react';
import API from '../Api';

// In-memory cache for pages
let pagesCache = null;
let pagesCacheTime = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Custom hook to fetch and cache pages
 * Prevents duplicate API calls across components
 * @returns {Object} { pages, loading, error, refetch }
 */
const usePages = () => {
	const [pages, setPages] = useState(pagesCache || []);
	const [loading, setLoading] = useState(!pagesCache);
	const [error, setError] = useState(null);

	const fetchPages = useCallback(async () => {
		// Check if cache is still valid
		if (pagesCache && pagesCacheTime) {
			const now = Date.now();
			if (now - pagesCacheTime < CACHE_DURATION) {
				setPages(pagesCache);
				setLoading(false);
				return pagesCache;
			}
		}

		setLoading(true);
		setError(null);

		try {
			const res = await API.get('pages/get');
			const pagesData = res.data.data || [];

			// Update cache
			pagesCache = pagesData;
			pagesCacheTime = Date.now();

			setPages(pagesData);
			setLoading(false);
			return pagesData;
		} catch (err) {
			setError(err);
			setLoading(false);

			// Still return cached data if available on error
			if (pagesCache) {
				setPages(pagesCache);
				return pagesCache;
			}
			return [];
		}
	}, []);

	useEffect(() => {
		fetchPages();
	}, [fetchPages]);

	// Manual refetch function to invalidate cache
	const refetch = useCallback(async () => {
		pagesCache = null;
		pagesCacheTime = null;
		return await fetchPages();
	}, [fetchPages]);

	return {
		pages,
		loading,
		error,
		refetch
	};
};

// Export function to clear cache (useful for testing or manual invalidation)
export const clearPagesCache = () => {
	pagesCache = null;
	pagesCacheTime = null;
	
};

export default usePages;
